import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  FiArrowRight,
  FiCheck,
  FiFeather,
  FiFileText,
  FiLayers,
  FiPackage,
  FiPenTool,
  FiTag,
  FiTruck,
  FiType,
  FiStar,
  FiPhone,
  FiChevronRight,
  FiMenu,
  FiX,
} from "react-icons/fi";
import logo from "../assets/melona logo.png";

const SERVICES = [
  {
    icon: <FiLayers />,
    title: "Business Cards",
    tagline: "Make every handshake count.",
    bestFor: "Founders, sales teams, events",
    features: ["Soft-touch laminate", "Foil stamping", "Duplex and spot UV", "Stocks up to 400gsm"],
    color: "#0F5D4F",
  },
  {
    icon: <FiFeather />,
    title: "Flyers & Leaflets",
    tagline: "Campaign materials that move quickly.",
    bestFor: "Promotions, launches, announcements",
    features: ["A4, A5, DL formats", "Gloss or matte", "Single or double-sided", "Short-run and bulk"],
    color: "#22A06B",
  },
  {
    icon: <FiTag />,
    title: "Banners & Signage",
    tagline: "Large-format impact for any space.",
    bestFor: "Storefronts, expos, outdoor ads",
    features: ["Vinyl and fabric", "Roll-up stands", "Indoor and outdoor grades", "Custom sizing"],
    color: "#D4AF37",
  },
  {
    icon: <FiCheck />,
    title: "Stickers & Labels",
    tagline: "Precision-cut for every product.",
    bestFor: "Packaging, jars, bottles, promos",
    features: ["Die-cut shapes", "Waterproof vinyl", "Foil options", "Roll or sheet formats"],
    color: "#22A06B",
  },
  {
    icon: <FiType />,
    title: "Brochures",
    tagline: "Tell a complete story in print.",
    bestFor: "Menus, profiles, catalogs",
    features: ["Saddle stitch", "Perfect-bound options", "Full-bleed support", "Premium coated stocks"],
    color: "#0F5D4F",
  },
  {
    icon: <FiPackage />,
    title: "Custom Packaging",
    tagline: "Packaging that sells before it opens.",
    bestFor: "Retail, gifting, product launches",
    features: ["Boxes and bags", "Branded tissue", "Embossing", "Low minimum runs"],
    color: "#D4AF37",
  },
  {
    icon: <FiPenTool />,
    title: "Design Support",
    tagline: "From rough idea to print-ready file.",
    bestFor: "Teams without final artwork",
    features: ["Artwork prep", "Digital proofing", "Pre-press checks", "Revision support"],
    color: "#22A06B",
  },
];

const QUOTE_STEPS = [
  { icon: <FiFileText />, title: "Share specs", body: "Product, size, quantity, finish, and deadline." },
  { icon: <FiPenTool />, title: "Approve proof", body: "We check artwork before production begins." },
  { icon: <FiTruck />, title: "Pickup or delivery", body: "Collect from Melona or arrange local handover." },
];

const ServicesPage = () => {
  const [active, setActive] = useState(SERVICES[0].title);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const selected = SERVICES.find((s) => s.title === active) || SERVICES[0];

  useEffect(() => {
    document.title = "Our Services - Melona Printing";
  }, []);

  return (
    <div className="melona-premium">
      {/* ── Navigation ── */}
      <nav className="melona-nav scrolled">
        <div className="melona-nav__inner">
          <Link to="/" className="melona-nav__logo">
            <img src={logo} alt="Melona" style={{ height: "40px" }} />
          </Link>
          <div className="melona-nav__links">
            <Link to="/">Home</Link>
            <Link to="/about">About</Link>
            <Link to="/services" className="active">Services</Link>
            <Link to="/#portfolio">Portfolio</Link>
            <Link to="/contact">Contact</Link>
            <Link to="/login">Login</Link>
          </div>
          <Link to="/contact" className="melona-nav__cta">
            <FiPhone /> Get Quote
          </Link>
          <button className="melona-nav__toggle" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Toggle menu">
            {mobileMenuOpen ? <FiX /> : <FiMenu />}
          </button>
        </div>
        
        {/* Mobile Menu Dropdown */}
        {mobileMenuOpen && (
          <div className="melona-mobile-menu">
            <nav>
              <Link to="/" onClick={() => setMobileMenuOpen(false)}>Home</Link>
              <Link to="/about" onClick={() => setMobileMenuOpen(false)}>About</Link>
              <Link to="/services" onClick={() => setMobileMenuOpen(false)} className="active">Services</Link>
              <Link to="/gallery" onClick={() => setMobileMenuOpen(false)}>Portfolio</Link>
              <Link to="/contact" onClick={() => setMobileMenuOpen(false)}>Contact</Link>
              <Link to="/login" onClick={() => setMobileMenuOpen(false)}>Login</Link>
            </nav>
            <Link to="/contact" className="melona-btn melona-btn--primary melona-btn--full" style={{ marginTop: "var(--sp-4)" }} onClick={() => setMobileMenuOpen(false)}>
              <FiPhone /> Get Free Quote
            </Link>
          </div>
        )}
      </nav>

      {/* ── Hero ── */}
      <section className="melona-services-hero">
        <div className="melona-container melona-services-hero__content">
          <span className="melona-eyebrow"><FiStar /> Our Services</span>
          <h1 className="melona-services-hero__title">
            Every Print Product Backed by<br />
            <span className="melona-services-hero__title-accent">Practical Production Control</span>
          </h1>
          <p className="melona-services-hero__subtitle">
            Choose the format you need, then Melona helps with material, finish,
            artwork checks, and delivery planning.
          </p>
          <div className="melona-services-hero__actions">
            <Link to="/contact" className="melona-btn melona-btn--primary">
              Request Pricing <FiArrowRight />
            </Link>
            <Link to="/#portfolio" className="melona-btn melona-btn--outline">
              See Examples
            </Link>
          </div>
          <div className="melona-services-hero__steps">
            {QUOTE_STEPS.map((step, i) => (
              <div key={i} className="melona-services-hero__step">
                <div className="melona-services-hero__step-icon">{step.icon}</div>
                <div>
                  <strong>{step.title}</strong>
                  <p>{step.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Service Tabs ── */}
      <section className="melona-section">
        <div className="melona-container">
          <div className="melona-section-header">
            <span className="melona-eyebrow"><FiLayers /> Service Menu</span>
            <h2 className="melona-section-title">Pick a product and see what matters.</h2>
            <p className="melona-section-subtitle">Tap a service to preview the right use case and finishing options.</p>
          </div>
          <div className="melona-services-layout">
            <div className="melona-services-tabs">
              {SERVICES.map((service) => (
                <button
                  key={service.title}
                  className={`melona-services-tab ${active === service.title ? "active" : ""}`}
                  style={{ "--svc-color": service.color }}
                  onClick={() => setActive(service.title)}
                >
                  <span className="melona-services-tab__icon">{service.icon}</span>
                  <span className="melona-services-tab__label">{service.title}</span>
                </button>
              ))}
            </div>
            <div className="melona-services-panel" style={{ "--svc-color": selected.color }}>
              <div className="melona-services-panel__header">
                <div className="melona-services-panel__icon">{selected.icon}</div>
                <div>
                  <p className="melona-services-panel__tagline">{selected.tagline}</p>
                  <h2 className="melona-services-panel__title">{selected.title}</h2>
                </div>
              </div>
              <div className="melona-services-panel__meta">
                <span className="melona-services-panel__meta-label">Best for</span>
                <span className="melona-services-panel__meta-value">{selected.bestFor}</span>
              </div>
              <ul className="melona-services-panel__features">
                {selected.features.map((f) => (
                  <li key={f}><FiCheck /> {f}</li>
                ))}
              </ul>
              <Link to="/contact" className="melona-btn melona-btn--primary">
                Quote This Service <FiArrowRight />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── All Services Grid ── */}
      <section className="melona-section melona-section--gray">
        <div className="melona-container">
          <div className="melona-section-header">
            <span className="melona-eyebrow"><FiPackage /> All Services</span>
            <h2 className="melona-section-title">Comprehensive Printing & Marketing Solutions</h2>
            <p className="melona-section-subtitle">From business cards to large-format advertising, we cover every need.</p>
          </div>
          <div className="melona-services-all-grid">
            {SERVICES.map((service, i) => (
              <div key={i} className="melona-service-card" style={{ "--accent": service.color }}>
                <div className="melona-service-card__icon">{service.icon}</div>
                <h3 className="melona-service-card__title">{service.title}</h3>
                <p className="melona-service-card__desc">{service.tagline}</p>
                <Link to="/contact" className="melona-service-card__link">
                  Get Quote <FiChevronRight />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA Strip ── */}
      <section className="melona-cta-strip">
        <div className="melona-container melona-cta-strip__inner">
          <div className="melona-cta-strip__icon"><FiFileText /></div>
          <div className="melona-cta-strip__text">
            <strong>Not sure which material or finish to choose?</strong>
            <span>Send your deadline and use case. We will suggest the practical option.</span>
          </div>
          <Link to="/contact" className="melona-btn melona-btn--gold">
            Ask Melona <FiArrowRight />
          </Link>
        </div>
      </section>

      {/* ── Footer ── */}
     
    </div>
  );
};

export default ServicesPage;